import { BigNumberSchema, mustNeverExist, SerializableSchema, z } from "@repo/utils";
import {
    PurchaseOrderConfirmationSchema,
    PurchaseOrderExtraCalculationSchema,
    PurchaseOrderExtraSchema,
    PurchaseOrderExtraTypeSchema,
} from "../../../erp/z.js";
import type { PurchaseOrderExtraCalculationClass, PurchaseOrderExtraType } from "../../../erp/types.js";
import type { OrderConfirmationExtractionExtraDefinition } from "./types.js";

export const DeliveryScheduleLineSchema = z.object({
    quantity: z.number(),
    deliveryDate: z.nullable(z.string()),
    shipmentDate: z.nullable(z.string()),
    deliveryDateMentioned: z.optional(z.boolean()),
    shipmentDateMentioned: z.optional(z.boolean()),
});

export const ExtractionDeliveryScheduleLineSchema = z.object({
    quantity: z.nullable(z.number()),
    deliveryDate: z.nullable(z.string()),
    shipmentDate: z.nullable(z.string()),
    deliveryDateMentioned: z.optional(z.boolean()),
    shipmentDateMentioned: z.optional(z.boolean()),
});

export const LLMExtractionDeliveryScheduleSchema = z.object({
    quantity: z.nullable(z.number()).register(z.globalRegistry, {
        description: "Item quantity if it's a single solid delivery or the specific delivery batch ",
    }),
    deliveryDate: z.nullable(z.string()).register(z.globalRegistry, {
        description:
            'The delivery date for the specific amount of the quantity. It could be an absolute date, a week number, or "in-stock".',
    }),
    shipmentDate: z.nullable(z.string()).register(z.globalRegistry, {
        description:
            'The shipment date for the specific amount of the quantity. It could be an absolute date, a week number, or "in-stock".',
    }),
    deliveryDateMentioned: z.boolean().register(z.globalRegistry, {
        description:
            "True if the supplier has mentioned a delivery date or explicitly stated that it's unknown and it will be defined in the future, etc. False if the supplier has completely skipped the delivery date. If there is a placeholder, such as a field label or column header for a delivery date, but no actual delivery date or reference to a delivery date provided, return False as well.",
    }),
    shipmentDateMentioned: z.boolean().register(z.globalRegistry, {
        description:
            "True if the supplier has mentioned a shipment date or explicitly stated that it's unknown and it will be defined in the future, etc. False if the supplier has completely skipped the shipment date. If there is a placeholder, such as a field label or column header for a shipment date, but no actual delivery date or reference to any shipment date provided, return False as well.",
    }),
});

export const buildConfirmationExtraSchema = (definitions: OrderConfirmationExtractionExtraDefinition[]) => {
    const map = definitions.reduce((acc, cur) => {
        const set = acc.get(cur.calculationClass) ?? new Set();
        set.add(cur.type);
        acc.set(cur.calculationClass, set);
        return acc;
    }, new Map<PurchaseOrderExtraCalculationClass, Set<PurchaseOrderExtraType>>());

    return z.union(
        Array.from(
            (function* () {
                for (const [c, types] of map) {
                    const type = z.enum(Array.from(types)).register(z.globalRegistry, {
                        description:
                            'The type of the extra item. If the confirmed discount or surcharge can\'t be match to any specific value from the list, use `"unknown"`',
                    });
                    switch (c) {
                        case "percentage":
                            yield z.object({
                                calculationClass: z.literal("percentage").register(z.globalRegistry, {
                                    description: "Indicates that the extra line is calculated as percentage",
                                }),
                                type,
                                description: z.string().register(z.globalRegistry, {
                                    description: "Short description of the extra line",
                                }),
                                rate: z.number().register(z.globalRegistry, {
                                    description:
                                        "Amount of percents that corresponds to the extra line. It must be negative for discounts and positive for surcharges",
                                }),
                                amount: z.number().register(z.globalRegistry, {
                                    description:
                                        "Monetary amount of the extra line. Must be calculated based on the rate if it's not explicitly mentioned",
                                }),
                                appliedTo: z.enum(["net", "gross"]).register(z.globalRegistry, {
                                    description: "Reason if the percentage is applied to the gross or the net value",
                                }),
                            });
                            break;
                        case "fixedAmount":
                            yield z
                                .object({
                                    calculationClass: z.literal("fixedAmount").register(z.globalRegistry, {
                                        description:
                                            "Indicates that the extra line is an absolute value (fixed amount)",
                                    }),
                                    type,
                                    description: z.string().register(z.globalRegistry, {
                                        description: "Short description of the extra line",
                                    }),
                                    amount: z.number().register(z.globalRegistry, {
                                        description:
                                            "Monetary amount of the extra line. Must be negative if discount and positive if surcharge",
                                    }),
                                })
                                .register(z.globalRegistry, {
                                    description:
                                        "The extra line contains only an absolute value of discount or surcharge",
                                });
                            break;
                        case "quantityBased":
                            yield z
                                .object({
                                    calculationClass: z.literal("quantityBased").register(z.globalRegistry, {
                                        description:
                                            "Indicates that the extra line is calculated based on some quantity",
                                    }),
                                    type,
                                    description: z.string().register(z.globalRegistry, {
                                        description: "Short description of the extra line",
                                    }),
                                    quantity: z.number().register(z.globalRegistry, {
                                        description: "Amount of quantity on what the extra line is based on",
                                    }),
                                    quantityUom: z.string().register(z.globalRegistry, {
                                        description:
                                            "The units of measure in what the quantity for the extra line is given. It might or might not have the same Uom as the item",
                                    }),
                                    unitPrice: z.number().register(z.globalRegistry, {
                                        description: "The price per unit of the quantity",
                                    }),
                                    priceUom: z.string().register(z.globalRegistry, {
                                        description:
                                            "The extra line unit price might be given per some specific units of measure. If it's not given explicitly, use the extra line quantity uom here",
                                    }),
                                    perAmount: z.number().register(z.globalRegistry, {
                                        description:
                                            "The amount of the quantity that represent one unit the unit price is given for. Example: 15 EUR/10 KG means 10 KG is the `perAmount` for the `unitPrice` 15 EUR",
                                    }),
                                    uomConversionCoefficient: z.number().register(z.globalRegistry, {
                                        description:
                                            "In the case the unit price is given in one unit and the quantity in another, try to figure out what is the conversion coefficient for converting price units to quantity units. If the units are eqaul or the conversion is not possible, use `1`",
                                    }),
                                    amount: z.number().register(z.globalRegistry, {
                                        description:
                                            "Monetary amount of the extra line. Must be negative if discount and positive if surcharge. Calculate if it's not given",
                                    }),
                                })
                                .register(z.globalRegistry, {
                                    description:
                                        "The extra line is based on the quantity and holds parts that are required to calculate the discount or surcharge",
                                });
                            break;
                        default:
                            throw mustNeverExist(c);
                    }
                }
            })(),
        ),
    );
};

export const buildConfirmationsLLMExtractionSchema = (extraDefinitions: OrderConfirmationExtractionExtraDefinition[]) =>
    z.object({
        confirmations: z
            .array(
                z.object({
                    header: z
                        .object({
                            number: z.nullable(z.string()).register(z.globalRegistry, {
                                description: "Uniq order confirmation number/code. `null` if it's not given",
                            }),
                            date: z.string().register(z.globalRegistry, {
                                description: "Date when the order was confirmed",
                            }),
                            currency: z.nullable(z.string()).register(z.globalRegistry, {
                                description: "Three-letter currency code (e.g., USD, EUR, GBP)",
                            }),
                            billingAddress: z.nullable(z.string()).register(z.globalRegistry, {
                                description:
                                    "Complete billing address including name, street, city, postal code, country",
                            }),
                            paymentTerms: z
                                .nullable(z.string())
                                .register(z.globalRegistry, { description: "Payment terms" }),
                            deliveryTerms: z
                                .object({
                                    incoterms: z.nullable(z.string()).register(z.globalRegistry, {
                                        description:
                                            'Well-known International delivery terms (Incoterms) like "DAP" or "Free On Board". Extract only if the supplier explicitly stated one of them, otherwise `null`',
                                    }),
                                    location: z.nullable(z.string()).register(z.globalRegistry, {
                                        description:
                                            "Location where the responsibilities are transferred from seller to buyer according to the supplier order confirmation. It must be explictly mentioned, otherwise `null`",
                                    }),
                                    additions: z.nullable(z.string()).register(z.globalRegistry, {
                                        description:
                                            'Any additions to the delivery terms that might be important for specific delivery terms e.g. "Prepaid and add", "Freight Collect", etc',
                                    }),
                                })
                                .register(z.globalRegistry, {
                                    description: "Delivery terms following international standards",
                                }),
                        })
                        .register(z.globalRegistry, {
                            description: "Header information containing order metadata",
                        }),
                    items: z
                        .array(
                            z.object({
                                kind: z
                                    .enum(["material", "service", "additional_cost", "other"])
                                    .register(z.globalRegistry, {
                                        description:
                                            'The kind of the item indicates if the item is a confirmed material, service, some additional costs. If the kind is unclear use "other"',
                                    }),
                                positionNumber: z.nullable(z.string()).register(z.globalRegistry, {
                                    description: "Line item position/sequence number in the order",
                                }),
                                poNumber: z.string().register(z.globalRegistry, {
                                    description:
                                        "Purchase order number associated with this specific line item, may differ from the header PO number in case of multi-order confirmations",
                                }),
                                description: z.nullable(z.string()).register(z.globalRegistry, {
                                    description: "Detailed description of the product or service",
                                }),
                                articleNumbers: z.array(z.string()).register(z.globalRegistry, {
                                    description:
                                        "List of article numbers or SKUs the supplier mentioned for the item. They could be their or our internal identification numbers",
                                }),
                                deliveryAddress: z.nullable(z.string()).register(z.globalRegistry, {
                                    description: "Address where the item should be delivered",
                                }),
                                deliverySchedules: z
                                    .array(LLMExtractionDeliveryScheduleSchema)
                                    .register(z.globalRegistry, {
                                        description:
                                            "Delivery schedule information - leave the array empty if no information about the delivery is provided, one element if only one delivery date is stated or many items if it's a split",
                                    }),
                                basePrice: z
                                    .object({
                                        quantity: z.nullable(z.number()).register(z.globalRegistry, {
                                            description: "Total quantity of items ordered across all delivery batches",
                                        }),
                                        quantityUom: z.nullable(z.string()).register(z.globalRegistry, {
                                            description:
                                                "Units of measurement in which the quantity is measured e.g for `100 KG` it's `KG`",
                                        }),
                                        priceUom: z.nullable(z.string()).register(z.globalRegistry, {
                                            description:
                                                "Units of measurement in which the price is measured. It's never can be the currency. If it's not given explicitly, assume the price is in the same units as the quantity. Examples: for `25 EUR/LB` it's `LB`, for `100 KG x 20 EUR` it's `KG`",
                                        }),
                                        unitPrice: z.nullable(z.number()).register(z.globalRegistry, {
                                            description:
                                                "Gross price for a single unit before any discount or surcharge. Calculate it if it's omitted",
                                        }),
                                        priceUnit: z.nullable(z.number()).register(z.globalRegistry, {
                                            description:
                                                'Number of units the price applies to. Example: "per 100" means the priceUnit is 100; "20 EUR/KG" means the priceUnit is 1; "20 EUR/10 KG means the priceUnit is 10',
                                        }),
                                        totalPrice: z.nullable(z.number()).register(z.globalRegistry, {
                                            description:
                                                "Total gross price of the line item before any discount or surcharges. Calculate it if it's omitted",
                                        }),
                                    })
                                    .register(z.globalRegistry, {
                                        description:
                                            "The base structure of the item price without any discounts and surcharges",
                                    }),
                                itemExtra: z
                                    .array(
                                        buildConfirmationExtraSchema(
                                            extraDefinitions.filter(
                                                x => x.placement === "both" || x.placement === "item",
                                            ),
                                        ),
                                    )
                                    .register(z.globalRegistry, {
                                        description:
                                            "Additional discounts and surcharges that are related to the position and really affect the position total value",
                                    }),
                            }),
                        )
                        .register(z.globalRegistry, {
                            description: "Array of items/products included in the order",
                        }),
                    headerExtra: z
                        .array(
                            buildConfirmationExtraSchema(
                                extraDefinitions.filter(x => x.placement === "both" || x.placement === "header"),
                            ),
                        )
                        .register(z.globalRegistry, {
                            description:
                                "Additional discounts and surcharges that are related to the entire order on the header level and really affect the final order value",
                        }),
                }),
            )
            .register(z.globalRegistry, {
                description: "Array of order confirmations extracted from emails",
            }),
    });

export const OrderConfirmationExtractionExtraSchema = z.object({
    serialNumber: z.number(),
    type: PurchaseOrderExtraTypeSchema,
    description: z.string(),
    amount: z.number(),
    calculation: PurchaseOrderExtraCalculationSchema,
});

export const OrderConfirmationsExtractionResultSchema = z.object({
    confirmations: z.array(
        z.object({
            header: z.object({
                number: z.nullable(z.string()),
                date: z.string(),
                currency: z.nullable(z.string()),
                billingAddress: z.nullable(z.string()),
                paymentTerms: z.nullable(z.string()),
                deliveryTerms: z.nullable(
                    z.object({
                        incoterms: z.nullable(z.string()),
                        location: z.nullable(z.string()),
                        additions: z.optional(z.nullable(z.string())),
                    }),
                ),
            }),
            items: z.array(
                z.object({
                    serialNumber: z.number(),
                    positionNumber: z.nullable(z.string()),
                    poNumber: z.string(),
                    description: z.nullable(z.string()),
                    articleNumbers: z.array(z.string()),
                    deliveryAddress: z.nullable(z.string()),
                    deliverySchedules: z.array(ExtractionDeliveryScheduleLineSchema),
                    costStructure: z.object({
                        quantity: z.nullable(z.number()),
                        quantityUom: z.nullable(z.string()),
                        unitPrice: z.nullable(z.number()),
                        priceUom: z.nullable(z.string()),
                        priceUnit: z.nullable(z.number()),
                        totalPrice: z.nullable(z.number()),
                    }),
                    itemExtra: z.array(OrderConfirmationExtractionExtraSchema),
                }),
            ),
            headerExtra: z.array(OrderConfirmationExtractionExtraSchema),
        }),
    ),
});

export const DeliveryTermsComparisonSchema = z
    .object({
        orderDeliveryTerms1: z.nullable(z.string()).register(z.globalRegistry, {
            description: "Delivery terms 1 as stated in the purchase order",
        }),
        orderDeliveryTerms2: z.nullable(z.string()).register(z.globalRegistry, {
            description: "Delivery terms 2 as stated in the purchase order",
        }),
        updateForDeliveryTerms1: z
            .nullable(z.string())
            .register(z.globalRegistry, { description: "Update proposal for delivery terms 1" }),
        updateForDeliveryTerms2: z
            .nullable(z.string())
            .register(z.globalRegistry, { description: "Update proposal for delivery terms 2" }),
    })
    .register(z.globalRegistry, {
        description: "The order delivery terms and update proposals them according to the specified comparision rules",
    });

export const LLMComparisonDeliveryScheduleSchema = z.object({
    quantity: z.number().register(z.globalRegistry, {
        description: "Item quantity if it's a single solid delivery or the specific delivery batch ",
    }),
    deliveryDate: z.nullable(z.string()).register(z.globalRegistry, {
        description:
            'The delivery date for the specific amount of the quantity. It could be an absolute date, a week number, or "in-stock".',
    }),
    shipmentDate: z.nullable(z.string()).register(z.globalRegistry, {
        description:
            'The shipment date for the specific amount of the quantity. It could be an absolute date, a week number, or "in-stock".',
    }),
    deliveryDateMentioned: z.boolean().register(z.globalRegistry, {
        description:
            "True if the supplier has mentioned a delivery date or explicitly stated that it's unknown and it will be defined in the future, etc. False if the supplier has completely skipped the delivery date. If there is a placeholder, such as a field label or column header for a delivery date, but no actual delivery date or reference to a delivery date provided, return False as well.",
    }),
    shipmentDateMentioned: z.boolean().register(z.globalRegistry, {
        description:
            "True if the supplier has mentioned a shipment date or explicitly stated that it's unknown and it will be defined in the future, etc. False if the supplier has completely skipped the shipment date. If there is a placeholder, such as a field label or column header for a shipment date, but no actual delivery date or reference to any shipment date provided, return False as well.",
    }),
});

export const LLMComparisonsSchemaV3 = z.object({
    matches: z.array(
        z.object({
            poId: z.string().register(z.globalRegistry, {
                description: "The identifier of the purchase order entity in the index",
            }),
            poNumber: z.string().register(z.globalRegistry, {
                description: "The reference number of the purchase order as displayed in documents",
            }),
            ocNumber: z.nullable(z.string()).register(z.globalRegistry, {
                description: "The reference number of the order confirmation as provided by the supplier",
            }),
            ocIndex: z.number().register(z.globalRegistry, {
                description: "The index of order confirmation from the extraction that corresponds the match",
            }),
            confirmationDate: z.string().register(z.globalRegistry, {
                description: "Date of the confirmation document in ISO format YYYY-MM-DD",
            }),
            head: z
                .object({
                    deliveryTerms: DeliveryTermsComparisonSchema,
                    paymentTerms: z
                        .object({
                            orderId: z.nullable(z.string()).register(z.globalRegistry, {
                                description:
                                    "Identifier for the payment terms as specified in the original purchase order. `null` indicates no ID was provided.",
                            }),
                            confirmationId: z.nullable(z.string()).register(z.globalRegistry, {
                                description:
                                    "Internal identifier that maps the supplier's confirmed payment terms to a known ERP payment terms entity. `null` if no mapping exists.",
                            }),
                        })
                        .register(z.globalRegistry, {
                            description:
                                "Structured representation of payment terms, including both the purchase order's original terms and the supplier's confirmation, along with corresponding identifiers.",
                        }),
                })
                .register(z.globalRegistry, {
                    description: "Confirmation details for header-level data from the purchase order",
                }),
            items: z
                .array(
                    z.discriminatedUnion("type", [
                        z.object({
                            type: z.literal("item_confirmed").register(z.globalRegistry, {
                                description: "Indicates this is a confirmation of an existing PO item",
                            }),
                            poItemId: z.string().register(z.globalRegistry, {
                                description: "The unique identifier of the purchase order item entity in the ERP",
                            }),
                            ocSerialNumber: z.number().register(z.globalRegistry, {
                                description: "The serial number of the extracted position that matches the po position",
                            }),
                            confirmedDeliverySchedules: z
                                .array(LLMComparisonDeliveryScheduleSchema)
                                .register(z.globalRegistry, {
                                    description:
                                        "Delivery schedule information - leave the array empty if no information about the delivery is provided, one element if only one delivery date is stated or many items if it's a split. Make sure the total item quantity and the cumulative delivery schedule quantity are aligned",
                                }),
                            deliveryAddress: z.object({
                                confirmedValue: z.nullable(z.string()).register(z.globalRegistry, {
                                    description: "The delivery address for the item as it's confirmed by the supplier",
                                }),
                                requireErpUpdate: z.boolean().register(z.globalRegistry, {
                                    description:
                                        "Flag indicating wether the address requires an update in the ERP. The address doesn't require update if the materials can be received at the expected place by the supplier specified delivery address",
                                }),
                            }),
                            uomConversion: z
                                .object({
                                    uomConversionRequired: z.boolean().register(z.globalRegistry, {
                                        description: "Indicate if the uom conversion is required",
                                    }),
                                    uomConverted: z.boolean().register(z.globalRegistry, {
                                        description:
                                            "Indicate if you have managed to convert the confirmed uom to the ordered ones",
                                    }),
                                    convertedQuantityInOrderQuantityUom: z
                                        .nullable(z.number())
                                        .register(z.globalRegistry, {
                                            description:
                                                "If you have managed to perform the conversion, write here the converted confirmed unit price",
                                        }),
                                    convertedUnitPriceInOrderPriceUom: z
                                        .nullable(z.number())
                                        .register(z.globalRegistry, {
                                            description:
                                                "If you have managed to perform the conversion, write here the converted confirmed unit price",
                                        }),
                                    convertedPriceUnitInOrderPriceUom: z
                                        .nullable(z.number())
                                        .register(z.globalRegistry, {
                                            description:
                                                "If you have managed to perform the conversion, write here the converted confirmed price unit",
                                        }),
                                })
                                .register(z.globalRegistry, {
                                    description:
                                        "Neither priceUoM nor quantityUoM can be changed ith purchase order. If it's required and possible, convert uom related components to match the order ones. For example, order: 10 EUR per 1 m x 30 cm; confirmation 10 EUR per 100cm x 0.26 m - the conversion must be 10 EUR per 1 m x 26 cm",
                                }),
                        }),
                        z.object({
                            type: z.literal("new_item").register(z.globalRegistry, {
                                description:
                                    "Indicates this is a new item added by the supplier that was not in the original PO",
                            }),
                            serialNumber: z.number().register(z.globalRegistry, {
                                description: "The serial number of the extracted item",
                            }),
                            number: z.nullable(z.string()).register(z.globalRegistry, {
                                description: "The number of the position as supplier states it",
                            }),
                            reason: z.nullable(z.string()).register(z.globalRegistry, {
                                description:
                                    "Reason why you considered the OC item has no matching item in the original PO",
                            }),
                        }),
                        z.object({
                            type: z.literal("missing_item").register(z.globalRegistry, {
                                description:
                                    "Indicates an item from the original PO that was not confirmed by the supplier",
                            }),
                            poItemId: z.string().register(z.globalRegistry, {
                                description: "The identifier of the purchase order item entity in the ERP",
                            }),
                            poItemNumber: z.string().register(z.globalRegistry, {
                                description: "The number of the item in the purchase order according to ERP data",
                            }),
                            reason: z.nullable(z.string()).register(z.globalRegistry, {
                                description:
                                    "Reason why you considered the PO item has no matching item in the extracted OC",
                            }),
                        }),
                    ]),
                )
                .register(z.globalRegistry, {
                    description: "Array of position confirmations including confirmed, new, and missing positions",
                }),
        }),
    ),
});

export const OrderConfirmationProcessingResultSchema = z
    .object({
        poId: z.string(),
        poNumber: z.string(),
        poDate: z.string(),
        ocNumber: z.string(),
        ocDate: z.string(),
        currency: z.string(),
        head: z.object({
            deliveryTerms: z.object({
                incoterms1: z.object({
                    orderValue: z.nullable(z.string()),
                    confirmedValue: z.nullable(z.string()),
                }),
                incoterms2: z.object({
                    orderValue: z.nullable(z.string()),
                    confirmedValue: z.nullable(z.string()),
                }),
            }),
            paymentTerms: z.object({
                orderId: z.nullable(z.string()),
                confirmationId: z.nullable(z.string()),
            }),
            supplierId: z.nullable(z.string()),
        }),
        items: z.array(
            z.discriminatedUnion("type", [
                z.object({
                    type: z.literal("item_confirmed"),
                    poItemId: z.string(),
                    poPositionNumber: z.string(),
                    ocPositionNumber: z.nullable(z.string()),
                    ocSerialNumber: z.number(),
                    materialId: z.nullable(z.string()),
                    description: z.string(),
                    supplierSku: z.object({
                        orderValue: z.nullable(z.string()),
                        confirmedValue: z.nullable(z.string()),
                    }),
                    orderDeliverySchedules: z.array(DeliveryScheduleLineSchema),
                    confirmedDeliverySchedules: z.array(DeliveryScheduleLineSchema),
                    deliveryAddress: z.object({
                        orderValue: z.nullable(z.string()),
                        confirmedValue: z.nullable(z.string()),
                        requiresUpdate: z.boolean(),
                    }),
                    costStructure: z.object({
                        orderQuantity: z.nullable(z.number()),
                        orderUnitPrice: z.nullable(z.number()),
                        orderPriceUnit: z.nullable(z.number()),
                        orderTotalPrice: z.nullable(z.number()),
                        orderQuantityUom: z.nullable(z.string()),
                        orderPriceUom: z.nullable(z.string()),
                        orderUomConversionCoefficient: z.number(),
                        confirmedQuantity: z.nullable(z.number()),
                        confirmedUnitPrice: z.nullable(z.number()),
                        confirmedPriceUnit: z.nullable(z.number()),
                        confirmedTotalPrice: z.nullable(z.number()),
                        confirmedQuantityUom: z.nullable(z.string()),
                        confirmedPriceUom: z.nullable(z.string()),
                        uomConversionRequired: z.boolean(),
                        convertedConfirmedQuantity: z.nullable(z.number()),
                        convertedConfirmedUnitPrice: z.nullable(z.number()),
                        convertedConfirmedPriceUnit: z.nullable(z.number()),
                        convertedConfirmedTotalPrice: z.nullable(z.number()),
                    }),
                    poExtra: z.array(PurchaseOrderExtraSchema),
                    ocExtra: z.array(OrderConfirmationExtractionExtraSchema),
                    confirmations: z.array(PurchaseOrderConfirmationSchema),
                }),
                z.object({
                    type: z.literal("new_item"),
                    number: z.nullable(z.string()),
                    serialNumber: z.number(),
                    materialId: z.nullable(z.string()),
                    description: z.nullable(z.string()),
                    supplierSku: z.nullable(z.string()),
                    deliverySchedules: z.array(DeliveryScheduleLineSchema),
                    deliveryAddress: z.nullable(z.string()),
                    quantity: z.nullable(BigNumberSchema),
                    quantityUom: z.nullable(z.string()),
                    unitPrice: z.nullable(BigNumberSchema),
                    priceUom: z.nullable(z.string()),
                    priceUnit: z.nullable(BigNumberSchema),
                    totalPrice: z.nullable(BigNumberSchema),
                    extra: z.array(OrderConfirmationExtractionExtraSchema),
                }),
                z.object({
                    type: z.literal("missing_item"),
                    id: z.string(),
                    number: z.string(),
                    materialId: z.nullable(z.string()),
                    description: z.string(),
                    supplierSku: z.nullable(z.string()),
                    deliverySchedules: z.array(DeliveryScheduleLineSchema),
                    deliveryAddress: z.nullable(z.string()),
                    quantity: z.nullable(BigNumberSchema),
                    quantityUom: z.nullable(z.string()),
                    unitPrice: z.nullable(BigNumberSchema),
                    priceUom: z.nullable(z.string()),
                    priceUnit: z.nullable(BigNumberSchema),
                    uomConversionCoefficient: z.number(),
                    totalPrice: z.nullable(BigNumberSchema),
                    confirmations: z.array(PurchaseOrderConfirmationSchema),
                    extra: z.array(PurchaseOrderExtraSchema),
                }),
            ]),
        ),
        poHeaderExtra: z.array(PurchaseOrderExtraSchema),
        ocHeaderExtra: z.array(OrderConfirmationExtractionExtraSchema),
        arbitrary: z.optional(z.record(z.string(), SerializableSchema)),
    })
    .register(z.globalRegistry, { title: "OrderConfirmationProcessingResultSchema" });

export function ValueDiscrepancySchema<T>(
    valueSchema: z.ZodMiniType<T>,
    comparisonType: "exact" | "fuzzy",
    comparisonDescription: string,
) {
    return z.object({
        orderValue: z.nullable(valueSchema).register(z.globalRegistry, {
            description: "The original value from the purchase order. `null` if it's not presented in the order",
        }),
        confirmedValue: z.nullable(valueSchema).register(z.globalRegistry, {
            description:
                "The confirmed value from the supplier confirmation. `null` if it's not presented in the confirmation",
        }),
        requiresUpdate: z.boolean().register(z.globalRegistry, {
            description:
                "Flag indicating whether the field requires an update in the ERP system based on the confirmed value. " +
                "When true, the discrepancy between order and confirmation is considered significant enough to warrant action. " +
                "If the confirmation value is null, no update is required and the flag must be false" +
                `For this field, using ${comparisonType} comparison.\n${comparisonDescription}`,
        }),
    });
}

export const CostStructureComparisonSchema = z.object({
    orderQuantity: z.nullable(z.number()).register(z.globalRegistry, {
        description: "The ordered quantity of the material. `null` if it's not presented in the order",
    }),
    orderUnitPrice: z.nullable(z.number()).register(z.globalRegistry, {
        description: "The price per unit of the material in the order. `null` if it's not presented in the order",
    }),
    orderPriceUnit: z.nullable(z.number()).register(z.globalRegistry, {
        description:
            "A numeric value that defines the quantity of items to which unit price applies in the order. `null` if it's not presented in the order",
    }),
    orderTotalPrice: z.nullable(z.number()).register(z.globalRegistry, {
        description: "The total price for the position in the order. `null` if it's not presented in the order",
    }),
    orderQuantityUom: z.nullable(z.string()).register(z.globalRegistry, {
        description: "The unit of measure for the ordered quantity. `null` if it's not presented in the order",
    }),
    orderPriceUom: z.nullable(z.string()).register(z.globalRegistry, {
        description: "The unit of measure for the price in the order. `null` if it's not presented in the order",
    }),
    confirmedQuantity: z.nullable(z.number()).register(z.globalRegistry, {
        description: "The confirmed quantity of the material. `null` if it's not confirmed",
    }),
    confirmedUnitPrice: z.nullable(z.number()).register(z.globalRegistry, {
        description: "The confirmed price per unit of the material. `null` if it's not confirmed",
    }),
    confirmedPriceUnit: z.nullable(z.number()).register(z.globalRegistry, {
        description:
            "A confirmed numeric value that defines the quantity of items to which unit price applies. `null` if it's not confirmed",
    }),
    confirmedTotalPrice: z.nullable(z.number()).register(z.globalRegistry, {
        description: "The confirmed total price for the position. `null` if it's not confirmed",
    }),
    confirmedQuantityUom: z.nullable(z.string()).register(z.globalRegistry, {
        description: "The confirmed unit of measure for the confirmed quantity. `null` if it's not confirmed",
    }),
    confirmedPriceUom: z.nullable(z.string()).register(z.globalRegistry, {
        description: "The confirmed unit of measure for the confirmed price. `null` if it's not confirmed",
    }),
    uomConversionRequired: z.boolean().register(z.globalRegistry, {
        description: "Flag indicating if unit of measure conversion is required between order and confirmation",
    }),
    uomConverted: z.boolean().register(z.globalRegistry, {
        description: "Flag indicating if the unit of measure conversion has been performed",
    }),
    priceUnitConverted: z._default(z.boolean(), false).register(z.globalRegistry, {
        description: "Flag if there is a converted price unit conversion in the cost structure",
    }),
    convertedConfirmedQuantity: z.nullable(z.number()).register(z.globalRegistry, {
        description:
            "The converted confirmed quantity of the material. Expected the field value is `null` if `uomConverted` and `priceUnitConverted` are false",
    }),
    convertedConfirmedUnitPrice: z.nullable(z.number()).register(z.globalRegistry, {
        description:
            "The converted confirmed price per unit of the material. Expected the field value is `null` if `uomConverted` and `priceUnitConverted` are false",
    }),
    convertedConfirmedPriceUnit: z.nullable(z.number()).register(z.globalRegistry, {
        description:
            "A converted confirmed numeric value that defines the quantity of items to which unit price applies. Expected the field value is `null` if `uomConverted` and `priceUnitConverted` are false",
    }),
    convertedConfirmedTotalPrice: z.nullable(z.number()).register(z.globalRegistry, {
        description:
            "The converted confirmed total price for the position. Expected the field value is `null` if `uomConverted` and `priceUnitConverted` are false",
    }),
});

export const PurchaseOrderComparisonSchema = z.object({
    poId: z.string().register(z.globalRegistry, {
        description: "The identifier of the purchase order entity in the index",
    }),
    poNumber: z.string().register(z.globalRegistry, {
        description: "The reference number of the purchase order as displayed in documents",
    }),
    ocNumber: z.nullable(z.string()).register(z.globalRegistry, {
        description: "The reference number of the order confirmation as provided by the supplier",
    }),
    confirmationDate: z.string().register(z.globalRegistry, {
        description: "Date of the confirmation document in ISO format YYYY-MM-DD",
    }),
    head: z
        .object({
            deliveryTerms: DeliveryTermsComparisonSchema,
            paymentTerms: z
                .object({
                    orderId: z.nullable(z.string()).register(z.globalRegistry, {
                        description:
                            "Identifier for the payment terms as specified in the original purchase order. `null` indicates no ID was provided.",
                    }),
                    confirmationId: z.nullable(z.string()).register(z.globalRegistry, {
                        description:
                            "Internal identifier that maps the supplier's confirmed payment terms to a known ERP payment terms entity. `null` if no mapping exists.",
                    }),
                })
                .register(z.globalRegistry, {
                    description:
                        "Structured representation of payment terms, including both the purchase order's original terms and the supplier's confirmation, along with corresponding identifiers.",
                }),
        })
        .register(z.globalRegistry, {
            description: "Confirmation details for header-level data from the purchase order",
        }),
    items: z
        .array(
            z.discriminatedUnion("type", [
                z.object({
                    type: z.literal("item_confirmed").register(z.globalRegistry, {
                        description: "Indicates this is a confirmation of an existing PO item",
                    }),
                    poItemId: z.string().register(z.globalRegistry, {
                        description: "The unique identifier of the purchase order item entity in the ERP",
                    }),
                    poPositionNumber: z.string().register(z.globalRegistry, {
                        description: "The line number of the item in the purchase order according to the ERP",
                    }),
                    ocPositionNumber: z.string().register(z.globalRegistry, {
                        description: "The number of the item in the order confirmation",
                    }),
                    ocSerialNumber: z.number().register(z.globalRegistry, {
                        description: "The serial number of the extracted position that matches the po position",
                    }),
                    supplierSku: ValueDiscrepancySchema(
                        z.string(),
                        "fuzzy",
                        'Supplier SKU requires update if the core identifying number/code differs. Ignore differences in formatting, prefixes/suffixes like "SKU:", "#", or separators',
                    ).register(z.globalRegistry, {
                        description: "Supplier's stock keeping unit identifier for the material",
                    }),
                    confirmedDeliverySchedules: z
                        .array(LLMComparisonDeliveryScheduleSchema)
                        .register(z.globalRegistry, {
                            description:
                                "Delivery schedule information - leave the array empty if no information about the delivery is provided, one element if only one delivery date is stated or many items if it's a split",
                        }),
                    deliveryAddress: ValueDiscrepancySchema(
                        z.string(),
                        "fuzzy",
                        "Delivery address doesn't require update if the materials can be received at the expected place by the supplier specified delivery address",
                    ).register(z.globalRegistry, {
                        description: "Address where the material will be delivered",
                    }),
                    costStructure: CostStructureComparisonSchema.register(z.globalRegistry, {
                        description: "The structure of the cost for the current position",
                    }),
                }),
                z.object({
                    type: z.literal("new_item").register(z.globalRegistry, {
                        description:
                            "Indicates this is a new item added by the supplier that was not in the original PO",
                    }),
                    serialNumber: z.number().register(z.globalRegistry, {
                        description: "The serial number of the extracted item",
                    }),
                    number: z.nullable(z.string()).register(z.globalRegistry, {
                        description: "The number of the position as supplier states it",
                    }),
                    materialId: z.nullable(z.string()).register(z.globalRegistry, {
                        description:
                            "The identifier of the material from the index that corresponds to the new position",
                    }),
                    description: z.string().register(z.globalRegistry, {
                        description: "Description of the material as provided by the supplier",
                    }),
                    supplierSku: z.nullable(z.string()).register(z.globalRegistry, {
                        description: "Supplier's stock keeping unit identifier for the material",
                    }),
                    deliverySchedules: z.array(LLMComparisonDeliveryScheduleSchema).register(z.globalRegistry, {
                        description:
                            "Delivery schedule information - leave the array empty if no information about the delivery is provided, one element if only one delivery date is stated or many items if it's a split",
                    }),
                    deliveryAddress: z.nullable(z.string()).register(z.globalRegistry, {
                        description: "Address where the material will be delivered",
                    }),
                    quantity: z
                        .nullable(z.number())
                        .register(z.globalRegistry, { description: "Quantity of the material" }),
                    quantityUom: z.nullable(z.string()).register(z.globalRegistry, {
                        description: "Unit of measure for the quantity",
                    }),
                    priceUom: z.nullable(z.string()).register(z.globalRegistry, {
                        description: "Unit of measure for the price",
                    }),
                    unitPrice: z.nullable(z.number()).register(z.globalRegistry, {
                        description: "Price per unit of the material",
                    }),
                    priceUnit: z.nullable(z.number()).register(z.globalRegistry, {
                        description:
                            "Quantity basis for the unit price (e.g., 1000 if price is per 1000 units, 100 if price is per 100 kg)",
                    }),
                    totalPrice: z.nullable(z.number()).register(z.globalRegistry, {
                        description: "Total price for this position",
                    }),
                }),
                z.object({
                    type: z.literal("missing_item").register(z.globalRegistry, {
                        description: "Indicates an item from the original PO that was not confirmed by the supplier",
                    }),
                    poItemId: z.string().register(z.globalRegistry, {
                        description: "The identifier of the purchase order item entity in the ERP",
                    }),
                    poItemNumber: z.string().register(z.globalRegistry, {
                        description: "The number of the item in the purchase order according to ERP data",
                    }),
                    reason: z.nullable(z.string()).register(z.globalRegistry, {
                        description: "Reason why item was not confirmed if known",
                    }),
                }),
            ]),
        )
        .register(z.globalRegistry, {
            description: "Array of position confirmations including confirmed, new, and missing positions",
        }),
});

export const PurchaseOrderComparisonsSchema = z.object({
    comparisons: z
        .array(PurchaseOrderComparisonSchema)
        .register(z.globalRegistry, { description: "Array of purchase order confirmations" }),
});

export const PurchaseOrderComparisonsSchemaV2 = z.object({
    comparisons: z
        .array(
            z.object({
                poId: z.string().register(z.globalRegistry, {
                    description: "The identifier of the purchase order entity in the index",
                }),
                poNumber: z.string().register(z.globalRegistry, {
                    description: "The reference number of the purchase order as displayed in documents",
                }),
                ocNumber: z.nullable(z.string()).register(z.globalRegistry, {
                    description: "The reference number of the order confirmation as provided by the supplier",
                }),
                confirmationDate: z.string().register(z.globalRegistry, {
                    description: "Date of the confirmation document in ISO format YYYY-MM-DD",
                }),
                head: z
                    .object({
                        deliveryTerms: DeliveryTermsComparisonSchema,
                        paymentTerms: z
                            .object({
                                orderId: z.nullable(z.string()).register(z.globalRegistry, {
                                    description:
                                        "Identifier for the payment terms as specified in the original purchase order. `null` indicates no ID was provided.",
                                }),
                                confirmationId: z.nullable(z.string()).register(z.globalRegistry, {
                                    description:
                                        "Internal identifier that maps the supplier's confirmed payment terms to a known ERP payment terms entity. `null` if no mapping exists.",
                                }),
                            })
                            .register(z.globalRegistry, {
                                description:
                                    "Structured representation of payment terms, including both the purchase order's original terms and the supplier's confirmation, along with corresponding identifiers.",
                            }),
                    })
                    .register(z.globalRegistry, {
                        description: "Confirmation details for header-level data from the purchase order",
                    }),
                items: z
                    .array(
                        z.discriminatedUnion("type", [
                            z.object({
                                type: z.literal("item_confirmed").register(z.globalRegistry, {
                                    description: "Indicates this is a confirmation of an existing PO item",
                                }),
                                poItemId: z.string().register(z.globalRegistry, {
                                    description: "The unique identifier of the purchase order item entity in the ERP",
                                }),
                                poPositionNumber: z.string().register(z.globalRegistry, {
                                    description:
                                        "The line number of the item in the purchase order according to the ERP",
                                }),
                                ocPositionNumber: z.string().register(z.globalRegistry, {
                                    description: "The number of the item in the order confirmation",
                                }),
                                ocSerialNumber: z.number().register(z.globalRegistry, {
                                    description:
                                        "The serial number of the extracted position that matches the po position",
                                }),
                                supplierSku: ValueDiscrepancySchema(
                                    z.string(),
                                    "fuzzy",
                                    'Supplier SKU requires update if the core identifying number/code differs. Ignore differences in formatting, prefixes/suffixes like "SKU:", "#", or separators',
                                ).register(z.globalRegistry, {
                                    description: "Supplier's stock keeping unit identifier for the material",
                                }),
                                deliveryAddress: ValueDiscrepancySchema(
                                    z.string(),
                                    "fuzzy",
                                    "Delivery address doesn't require update if the materials can be received at the expected place by the supplier specified delivery address",
                                ).register(z.globalRegistry, {
                                    description: "Address where the material will be delivered",
                                }),
                            }),
                            z.object({
                                type: z.literal("new_item").register(z.globalRegistry, {
                                    description:
                                        "Indicates this is a new item added by the supplier that was not in the original PO",
                                }),
                                serialNumber: z.number().register(z.globalRegistry, {
                                    description: "The serial number of the extracted item",
                                }),
                                number: z.nullable(z.string()).register(z.globalRegistry, {
                                    description: "The number of the position as supplier states it",
                                }),
                                reason: z.nullable(z.string()).register(z.globalRegistry, {
                                    description:
                                        "Reason why you considered the OC item has no matching item in the original PO",
                                }),
                            }),
                            z.object({
                                type: z.literal("missing_item").register(z.globalRegistry, {
                                    description:
                                        "Indicates an item from the original PO that was not confirmed by the supplier",
                                }),
                                poItemId: z.string().register(z.globalRegistry, {
                                    description: "The identifier of the purchase order item entity in the ERP",
                                }),
                                poItemNumber: z.string().register(z.globalRegistry, {
                                    description: "The number of the item in the purchase order according to ERP data",
                                }),
                                reason: z.nullable(z.string()).register(z.globalRegistry, {
                                    description:
                                        "Reason why you considered the PO item has no matching item in the extracted OC",
                                }),
                            }),
                        ]),
                    )
                    .register(z.globalRegistry, {
                        description: "Array of position confirmations including confirmed, new, and missing positions",
                    }),
            }),
        )
        .register(z.globalRegistry, { description: "Array of purchase order confirmations" }),
});

export function ValueDiscrepancyResultSchema<T>(valueSchema: z.ZodMiniType<T>) {
    return z.object({
        orderValue: z.nullable(valueSchema),
        confirmedValue: z.nullable(valueSchema),
        requiresUpdate: z.boolean(),
    });
}

export const PurchaseOrderProcessingResultSchema = z.object({
    poId: z.string(),
    poNumber: z.string(),
    poDate: z.string(),
    ocNumber: z.string(),
    ocDate: z.string(),
    currency: z.string(),
    head: z.object({
        deliveryTerms: z.object({
            incoterms1: ValueDiscrepancyResultSchema(z.string()),
            incoterms2: ValueDiscrepancyResultSchema(z.string()),
        }),
        paymentTerms: z.object({
            orderId: z.nullable(z.string()),
            confirmationId: z.nullable(z.string()),
        }),
    }),
    items: z.array(
        z.discriminatedUnion("type", [
            z.object({
                type: z.literal("item_confirmed"),
                poItemId: z.string(),
                poPositionNumber: z.string(),
                ocPositionNumber: z.nullable(z.string()),
                // !
                ocSerialNumber: z.number(),
                materialId: z.nullable(z.string()),
                description: z.string(),
                supplierSku: ValueDiscrepancyResultSchema(z.string()),
                orderDeliverySchedules: z.array(DeliveryScheduleLineSchema),
                confirmedDeliverySchedules: z.array(DeliveryScheduleLineSchema),
                deliveryAddress: ValueDiscrepancyResultSchema(z.string()),
                costStructure: z.object({
                    orderQuantity: z.nullable(z.number()),
                    orderUnitPrice: z.nullable(z.number()),
                    orderPriceUnit: z.nullable(z.number()),
                    orderTotalPrice: z.nullable(z.number()),
                    orderQuantityUom: z.nullable(z.string()),
                    orderPriceUom: z.nullable(z.string()),
                    orderUomConversionCoefficient: z.number(),
                    confirmedQuantity: z.nullable(z.number()),
                    confirmedUnitPrice: z.nullable(z.number()),
                    confirmedPriceUnit: z.nullable(z.number()),
                    confirmedTotalPrice: z.nullable(z.number()),
                    confirmedQuantityUom: z.nullable(z.string()),
                    confirmedPriceUom: z.nullable(z.string()),
                    uomConversionRequired: z.boolean(),
                    convertedConfirmedQuantity: z.nullable(z.number()),
                    convertedConfirmedUnitPrice: z.nullable(z.number()),
                    convertedConfirmedPriceUnit: z.nullable(z.number()),
                    convertedConfirmedTotalPrice: z.nullable(z.number()),
                }),
                confirmations: z.array(PurchaseOrderConfirmationSchema),
            }),
            z.object({
                type: z.literal("new_item"),
                number: z.nullable(z.string()),
                // !
                serialNumber: z.number(),
                materialId: z.nullable(z.string()),
                description: z.string(),
                supplierSku: z.nullable(z.string()),
                deliverySchedules: z.array(DeliveryScheduleLineSchema),
                deliveryAddress: z.nullable(z.string()),
                quantity: z.nullable(BigNumberSchema),
                quantityUom: z.nullable(z.string()),
                unitPrice: z.nullable(BigNumberSchema),
                priceUom: z.nullable(z.string()),
                priceUnit: z.nullable(BigNumberSchema),
                totalPrice: z.nullable(BigNumberSchema),
            }),
            z.object({
                type: z.literal("missing_item"),
                id: z.string(),
                number: z.string(),
                materialId: z.nullable(z.string()),
                description: z.string(),
                supplierSku: z.nullable(z.string()),
                deliverySchedules: z.array(DeliveryScheduleLineSchema),
                deliveryAddress: z.nullable(z.string()),
                quantity: z.nullable(BigNumberSchema),
                quantityUom: z.nullable(z.string()),
                unitPrice: z.nullable(BigNumberSchema),
                priceUom: z.nullable(z.string()),
                priceUnit: z.nullable(BigNumberSchema),
                uomConversionCoefficient: z.number(),
                totalPrice: z.nullable(BigNumberSchema),
                confirmations: z.array(PurchaseOrderConfirmationSchema),
            }),
        ]),
    ),
    extra: z.array(
        z.object({
            type: z.enum(["discount", "extraCosts"]),
            description: z.nullable(z.string()),
            amount: z.nullable(z.number()),
        }),
    ),
    arbitrary: z.optional(z.record(z.string(), SerializableSchema)),
});

export const ConfirmationTypeLLMExtractionSchema = z.object({
    orderReferences: z
        .array(
            z.object({
                orderNumber: z.string().register(z.globalRegistry, {
                    description: "The purchase order number referenced in the document or email",
                }),
                confirmationType: z.enum(["plainText", "itemized"]).register(z.globalRegistry, {
                    description: "The type of order confirmation for this purchase order",
                }),
            }),
        )
        .register(z.globalRegistry, {
            description:
                "List of all purchase order references found in the document or email, each classified by its confirmation type",
        }),
});
